import {
  Box,
  Dialog,
  DialogProps,
  IconButton,
  Slide,
  Stack,
} from '@mui/material';
import { getColor } from '../../assets/theme';
import { CloseOutlined } from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import React, { ReactNode } from 'react';

/**
 * Custom transition component for the Dialog.
 * It creates a sliding upward animation when the dialog opens.
 */
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction='up' ref={ref} {...props} />;
});

/**
 * Interface extending DialogProps with additional properties.
 */
interface DialogInterface extends DialogProps {
  /** Determines if the dialog is open */
  open: boolean;
  /** Function to be called when the dialog is closed */
  onClose?: () => void;
  /** Content to be rendered inside the dialog */
  children: ReactNode;
}

/**
 * DialogWrapper component that provides a customizable full-screen dialog.
 *
 * @param {DialogInterface} props - The props for the DialogWrapper component
 * @returns {React.ReactElement} The rendered DialogWrapper component
 */
const DialogWrapper: React.FC<DialogInterface> = ({
  open,
  onClose,
  children,
  sx,
}) => {
  const { backDrop } = getColor();
  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      sx={{
        '.MuiPaper-elevation': {
          bgcolor: backDrop,
        },
        ...sx,
      }}
    >
      {onClose && (
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', height: '90px' }}
        >
          <IconButton
            disableRipple
            sx={{
              margin: '21px',
              height: 'fit-content',
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText',
              ':hover': {
                bgcolor: 'secondary.contrastText',
                color: 'secondary.main',
              },
            }}
            onClick={onClose}
          >
            <CloseOutlined />
          </IconButton>
        </Box>
      )}

      <Stack
        className='dialog-wrapper'
        alignItems='center'
        justifyContent='center'
        height='100dvh'
        p={2}
      >
        {children}
      </Stack>
    </Dialog>
  );
};

export default DialogWrapper;
